<template>
  <!-- ============================================ -->
  <!-- PARTNER SERVICES - Compacto, una sola card  -->
  <!-- ============================================ -->
  <section class="relative py-14 sm:py-20 overflow-hidden">
    <!-- Background idéntico a FeaturesAndAudience -->
    <div class="absolute inset-0">
      <div
        class="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style="background-image: url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80')"
      ></div>
      <div class="absolute inset-0 bg-gray-950/95"></div>
      <div class="absolute inset-0 opacity-[0.03]" style="background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')"></div>
    </div>

    <div class="relative z-10 px-0 sm:px-6 lg:px-8">
      <div class="max-w-3xl mx-auto">

        <!-- Header -->
        <div class="text-center mb-8">
          <div class="inline-flex items-center gap-2 bg-indigo-500/15 border border-indigo-500/25 text-indigo-300 px-3 py-1 rounded-full text-xs font-semibold mb-4 uppercase tracking-wide">
            <span class="w-1.5 h-1.5 rounded-full bg-indigo-400 inline-block"></span>
            {{ $t('partnerServices.sectionBadge') }}
          </div>
          <h2 class="text-2xl sm:text-3xl font-bold text-white mb-2 leading-tight tracking-tight">
            {{ $t('partnerServices.title') }}
            <span class="text-transparent bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text">
              <span v-if="statsLoaded">{{ formattedSearchCount }} {{ $t('partnerServices.titleHighlight') }}</span>
              <span v-else>{{ $t('partnerServices.titleHighlightFallback') }}</span>
            </span>
            {{ $t('partnerServices.titleSuffix') }}
          </h2>
          <p class="text-sm text-gray-400">{{ $t('partnerServices.subtitle') }}</p>
        </div>

        <!-- CTA prominente -->
        <div class="mb-8 text-center">
          <button
            @click="openModal('marketplace')"
            class="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-colors duration-200 text-sm"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {{ $t('partnerServices.ctaButton') }}
          </button>
          <p class="mt-3 text-xs text-gray-500">
            {{ $t('partnerServices.ctaSubtext') }}
          </p>
        </div>

        <!-- Card única con las opciones de servicio -->
        <div class="bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden">
          <template v-for="key in serviceOrder" :key="key">
            <router-link
              v-if="key === 'lead'"
              to="/leads-marketplace"
              class="w-full flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-4 text-left transition-colors duration-150 border-b border-white/[0.07] last:border-b-0 group hover:bg-white/[0.05]"
              @click="trackInteraction('partner_service_click', { service: key, source: 'services_section' })"
            >
              <div class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" :class="serviceStyle(key).iconBgDark">
                <svg class="w-4 h-4 flex-shrink-0" :class="serviceStyle(key).iconColorDark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="serviceStyle(key).iconPath" />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm font-semibold text-white">{{ $t(`partnerServices.services.${key}.title`) }}</span>
                </div>
                <p class="text-xs text-gray-500 mt-0.5 line-clamp-2 sm:truncate">{{ $t(`partnerServices.services.${key}.shortDesc`) }}</p>
              </div>
              <svg class="w-4 h-4 text-gray-600 group-hover:text-gray-300 group-hover:translate-x-0.5 transition-all flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </router-link>
            <button
              v-else
              type="button"
              class="w-full flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-4 text-left transition-colors duration-150 border-b border-white/[0.07] last:border-b-0 group hover:bg-white/[0.05]"
              @click="openModal(key)"
            >
              <div class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" :class="serviceStyle(key).iconBgDark">
                <svg class="w-4 h-4 flex-shrink-0" :class="serviceStyle(key).iconColorDark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="serviceStyle(key).iconPath" />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm font-semibold text-white">{{ $t(`partnerServices.services.${key}.title`) }}</span>
                  <span
                    v-if="serviceStyle(key).badgeKey"
                    class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                    :class="serviceStyle(key).badgeClassDark"
                  >
                    {{ $t(`partnerServices.services.${key}.badge`) }}
                  </span>
                </div>
                <p class="text-xs text-gray-500 mt-0.5 line-clamp-2 sm:truncate">{{ $t(`partnerServices.services.${key}.shortDesc`) }}</p>
              </div>
              <svg class="w-4 h-4 text-gray-600 group-hover:text-gray-300 group-hover:translate-x-0.5 transition-all flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </template>
        </div>

      </div>
    </div>
  </section>

  <PartnerContactModal
    :isVisible="isModalVisible"
    :initialService="selectedServiceKey"
    @close="isModalVisible = false"
  />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import PartnerContactModal from './PartnerContactModal.vue'
import { trackInteraction } from '@/utils/trackInteraction'
import { PARTNER_SERVICE_ORDER, getPartnerServiceStyle } from '@/config/partnerServiceCatalog'

// ── Stats dinámicas ───────────────────────────────────────────────────────────
const searchesLast30d = ref(0)
const statsLoaded = ref(false)

const formattedSearchCount = computed(() => {
  const n = searchesLast30d.value
  if (n <= 0) return null
  return (Math.floor(n / 100) * 100).toLocaleString('es-ES') + '+'
})

onMounted(async () => {
  try {
    const res = await fetch('/api/analytics/public-stats')
    if (res.ok) {
      const data = await res.json()
      if (data.searchesLast30d > 0) {
        searchesLast30d.value = data.searchesLast30d
        statsLoaded.value = true
      }
    }
  } catch {
    // fallback silencioso
  }
})

// ── Modal ────────────────────────────────────────────────────────────────────
const isModalVisible = ref(false)
const selectedServiceKey = ref('marketplace')

const serviceOrder = PARTNER_SERVICE_ORDER

function serviceStyle(key) {
  return getPartnerServiceStyle(key)
}

function openModal(key) {
  trackInteraction('partner_service_click', { service: key, source: 'services_section' })
  selectedServiceKey.value = key
  isModalVisible.value = true
}
</script>
